/**
 * FSD Part 4 — Procurement (docs §19161). C17: one section per FSD feature
 * specification — 4.2.1 (PRC-F01), 4.2.2 (PRC-F02), 4.2.3 (PRC-F03), 4.2.5
 * (PRC-F05), 4.2.6 (PRC-F06), 4.2.7 (PRC-F07), 4.2.8 (PRC-F08). Section
 * titles come from `nav.fsd.*`. 4.2.4 has no section here: no current nav
 * item is assigned to it.
 *
 * Detail routes reached from a list or a document's own action rail — the
 * receipt inspection banner, a single supplier or contract, releases,
 * amendments and delivery schedules on a specific order, an RFQ's own
 * evaluation tab — are not nav entries.
 *
 * Item ids are stable and must stay so: favourites and recents in
 * localStorage key off them. Sections moved; items did not change.
 */
import type { NavGroup } from '../types'
import { PROCUREMENT_PERMISSIONS } from '@/modules/procurement/permissions'

export const P04_PROCUREMENT_GROUPS: NavGroup[] = [
  {
    id: 's-4.2.1',
    kind: 'feature',
    fsd: '4.2.1',
    code: 'PRC-F01',
    labelKey: 'nav.fsd.s4_2_1.short',
    fullTitleKey: 'nav.fsd.s4_2_1.full',
    items: [
      {
        id: 'proc-requisitions',
        labelKey: 'procurement.routeTitles.requisitions',
        to: '/procurement/requisitions',
        icon: 'inbox',
        permission: PROCUREMENT_PERMISSIONS.prRead,
        descriptionKey: 'nav.items.procRequisitions.description',
      },
    ],
  },
  {
    id: 's-4.2.2',
    kind: 'feature',
    fsd: '4.2.2',
    code: 'PRC-F02',
    labelKey: 'nav.fsd.s4_2_2.short',
    fullTitleKey: 'nav.fsd.s4_2_2.full',
    items: [
      {
        id: 'proc-suppliers',
        labelKey: 'procurement.c7.routeTitles.suppliers',
        to: '/procurement/suppliers',
        icon: 'users',
        permission: PROCUREMENT_PERMISSIONS.supplierRead,
      },
      {
        id: 'proc-supplier-policy',
        labelKey: 'procurement.c7.routeTitles.supplierPolicy',
        to: '/procurement/supplier-policies',
        icon: 'settings',
        permission: PROCUREMENT_PERMISSIONS.settingsManage,
      },
      {
        id: 'proc-supplier-risk',
        labelKey: 'procurement.c7.routeTitles.supplierRisk',
        to: '/procurement/reports/supplier-risk',
        icon: 'shield',
        permission: PROCUREMENT_PERMISSIONS.reportRead,
      },
      {
        id: 'proc-scorecards',
        labelKey: 'procurement.routeTitles.scorecards',
        to: '/procurement/vendor-scorecards',
        icon: 'star',
        permission: PROCUREMENT_PERMISSIONS.scorecardRead,
        descriptionKey: 'nav.items.procScorecards.description',
      },
    ],
  },
  {
    id: 's-4.2.3',
    kind: 'feature',
    fsd: '4.2.3',
    code: 'PRC-F03',
    labelKey: 'nav.fsd.s4_2_3.short',
    fullTitleKey: 'nav.fsd.s4_2_3.full',
    items: [
      {
        id: 'proc-rfqs',
        labelKey: 'procurement.routeTitles.rfqs',
        to: '/procurement/rfqs',
        icon: 'mail',
        permission: PROCUREMENT_PERMISSIONS.rfqRead,
        descriptionKey: 'nav.items.procRfqs.description',
      },
    ],
  },
  {
    id: 's-4.2.5',
    kind: 'feature',
    fsd: '4.2.5',
    code: 'PRC-F05',
    labelKey: 'nav.fsd.s4_2_5.short',
    fullTitleKey: 'nav.fsd.s4_2_5.full',
    items: [
      {
        id: 'proc-orders',
        labelKey: 'procurement.routeTitles.orders',
        to: '/procurement/orders',
        icon: 'link',
        permission: PROCUREMENT_PERMISSIONS.poRead,
        descriptionKey: 'nav.items.procOrders.description',
      },
      {
        id: 'proc-incoterms',
        labelKey: 'procurement.c7.routeTitles.incoterms',
        to: '/procurement/incoterms',
        icon: 'globe',
        permission: PROCUREMENT_PERMISSIONS.poRead,
      },
    ],
  },
  {
    id: 's-4.2.6',
    kind: 'feature',
    fsd: '4.2.6',
    code: 'PRC-F06',
    labelKey: 'nav.fsd.s4_2_6.short',
    fullTitleKey: 'nav.fsd.s4_2_6.full',
    items: [
      {
        id: 'proc-receipts',
        labelKey: 'procurement.routeTitles.receipts',
        to: '/procurement/receipts',
        icon: 'check',
        permission: PROCUREMENT_PERMISSIONS.grnRead,
        descriptionKey: 'nav.items.procReceipts.description',
      },
      {
        id: 'proc-gr-ir',
        labelKey: 'procurement.routeTitles.grIrAging',
        to: '/procurement/reports/gr-ir-aging',
        icon: 'clock',
        permission: PROCUREMENT_PERMISSIONS.reportRead,
        descriptionKey: 'nav.items.procGrIr.description',
      },
      {
        id: 'proc-returns',
        labelKey: 'procurement.c7.routeTitles.returns',
        to: '/procurement/supplier-returns',
        icon: 'refresh',
        permission: PROCUREMENT_PERMISSIONS.grnRead,
      },
    ],
  },
  {
    id: 's-4.2.7',
    kind: 'feature',
    fsd: '4.2.7',
    code: 'PRC-F07',
    labelKey: 'nav.fsd.s4_2_7.short',
    fullTitleKey: 'nav.fsd.s4_2_7.full',
    items: [
      {
        id: 'proc-matches',
        labelKey: 'procurement.routeTitles.matches',
        to: '/procurement/matches',
        icon: 'shieldCheck',
        permission: PROCUREMENT_PERMISSIONS.matchRead,
        descriptionKey: 'nav.items.procMatches.description',
      },
      {
        id: 'proc-match-exceptions',
        labelKey: 'procurement.c7.routeTitles.matchExceptions',
        to: '/procurement/match-exceptions',
        icon: 'shieldCheck',
        permission: PROCUREMENT_PERMISSIONS.matchRead,
      },
      {
        id: 'proc-match-causes',
        labelKey: 'procurement.c7.routeTitles.matchCauses',
        to: '/procurement/match-causes',
        icon: 'settings',
        permission: PROCUREMENT_PERMISSIONS.matchRead,
      },
      /* Procurement's own settings — match tolerances, approval bands, the
       * defaults the screens above obey. It reads on
       * `procurement.settings.manage` because the screen is the editor: there
       * is no read-only view of it to offer. */
      {
        id: 'proc-settings',
        labelKey: 'procurement.routeTitles.settings',
        to: '/procurement/settings',
        icon: 'settings',
        permission: PROCUREMENT_PERMISSIONS.settingsManage,
      },
    ],
  },
  {
    id: 's-4.2.8',
    kind: 'feature',
    fsd: '4.2.8',
    code: 'PRC-F08',
    labelKey: 'nav.fsd.s4_2_8.short',
    fullTitleKey: 'nav.fsd.s4_2_8.full',
    items: [
      {
        id: 'proc-contracts',
        labelKey: 'procurement.c7.routeTitles.contracts',
        to: '/procurement/contracts',
        icon: 'link',
        permission: PROCUREMENT_PERMISSIONS.contractRead,
      },
      {
        id: 'proc-contract-expiry',
        labelKey: 'procurement.c7.routeTitles.contractExpiry',
        to: '/procurement/reports/contract-expiry',
        icon: 'clock',
        permission: PROCUREMENT_PERMISSIONS.contractRead,
      },
      {
        id: 'proc-spend-analysis',
        labelKey: 'procurement.c7.routeTitles.spendAnalysis',
        to: '/procurement/spend-analysis',
        icon: 'scale',
        permission: PROCUREMENT_PERMISSIONS.spendRead,
      },
      {
        id: 'proc-maverick',
        labelKey: 'procurement.c7.routeTitles.maverick',
        to: '/procurement/spend-analysis/maverick',
        icon: 'alert',
        permission: PROCUREMENT_PERMISSIONS.spendRead,
      },
      {
        id: 'proc-spend-facts',
        labelKey: 'procurement.c7.routeTitles.spendFacts',
        to: '/procurement/spend-facts',
        icon: 'sitemap',
        permission: PROCUREMENT_PERMISSIONS.spendRead,
      },
    ],
  },
]
